import { writable } from "svelte/store";
import { createDeferredPromise } from "..";

type DialogConfirm = () => void;
type DialogClose = (reason?: string) => void;

export type DialogButton = {
    label: string;
    action: (confirm: DialogConfirm, close: DialogClose) => void;
};

export type Dialog = {
    id: string;
    title: string;
    content?: string;
    fields?: Record<string, DialogField>;

    buttons: DialogButton[];
    resolve: (value: any) => void;
    reject: (reason: any) => void;
};

type DialogFieldBase<T, Type extends string> = {
    type: Type;
    name: string;
    value: T;
};

type DialogFieldEnum = DialogFieldBase<string, "enum"> & {
    options?: string[];
};

export type DialogFieldCustom = DialogFieldBase<any, "custom"> & {
    component: any;
    props?: Record<string, any>;
};

export type DialogField =
    | DialogFieldBase<string, "string">
    | DialogFieldBase<string, "text">
    | DialogFieldBase<string, "html">
    | DialogFieldBase<string, "warning">
    | DialogFieldBase<number, "number">
    | DialogFieldBase<boolean, "boolean">
    | DialogFieldEnum
    | DialogFieldCustom;

export const dialogs = writable<Dialog[]>([]);

type DialogFieldTypesBase = {
    [key: string]: unknown;
};

type DialogFields<DialogFieldTypes extends DialogFieldTypesBase> = {
    [key in keyof DialogFieldTypes]: DialogField & {
        value: DialogFieldTypes[key];
    };
};

type DialogConfiguration<DialogFieldTypes extends DialogFieldTypesBase> = {
    id: string;
    title: string;
    content?: string;
    fields?: DialogFields<DialogFieldTypes>;
    buttons?: DialogButton[];
};

export function createDialog<DialogFieldTypes extends DialogFieldTypesBase>(
    config: DialogConfiguration<DialogFieldTypes>
): Promise<DialogFieldTypes> | undefined;
export function createDialog<DialogFieldTypes extends DialogFieldTypesBase>(
    config: DialogConfiguration<DialogFieldTypes>,
    throwOnClose: true
): Promise<DialogFieldTypes>;
export function createDialog<DialogFieldTypes extends DialogFieldTypesBase>(
    config: DialogConfiguration<DialogFieldTypes>,
    throwOnClose: false
): Promise<DialogFieldTypes> | undefined;

export function createDialog<DialogFieldTypes extends DialogFieldTypesBase>(
    config: DialogConfiguration<DialogFieldTypes>,
    throwOnClose?: boolean
): Promise<DialogFieldTypes> | undefined {
    const [promise, resolve, reject] = createDeferredPromise<any>();
    const { id, title, content, fields, buttons } = config;

    function remove() {
        dialogs.update((d) => {
            const index = d.findIndex((d) => d.id === id);
            if (index !== -1) {
                d.splice(index, 1);
            }
            return d;
        });
    }

    function res(arg: any) {
        remove();
        resolve(arg);
    }

    function rej(arg: any) {
        remove();
        reject(arg);
    }

    dialogs.update((d) => {
        d.push({
            id,
            title,
            content,
            fields,

            buttons: buttons ?? [DialogButton.cancel, DialogButton.confirm],
            resolve: res,
            reject: rej,
        });
        return d;
    });

    if (throwOnClose ?? false) {
        return promise as Promise<DialogFieldTypes>;
    } else {
        return promise.catch((_) => {}) as Promise<DialogFieldTypes> | undefined;
    }
}

export const createDialogButton = {
    confirm: (label: string) =>
        ({
            label,
            action: (confirm) => {
                confirm();
            },
        }) as DialogButton,
    close: (label: string) =>
        ({
            label,
            action: (confirm, close) => {
                close();
            },
        }) as DialogButton,
};

const DialogButton = {
    cancel: createDialogButton.close("Cancel"),
    confirm: createDialogButton.confirm("Confirm"),
};
